import { Controller } from "@hotwired/stimulus"
import { queueSummary } from "../field_sync_state"
import { currentLevel, LEVEL_DEGRADED } from "../field_network_health"
import { SYNC_LOCK_NAME } from "../field_sync_shared"

// Aggregate sync banner for the field app (ADR-0048 §1, W3.4).
//
// The "all clear / online call-to-action" surface. Reads the shared queue
// summary and renders exactly ONE of five states:
//
//   failures present                        -> red "needs attention" bar (scrolls to the list on /field/sync, links there elsewhere)
//   Degraded (ADR-0110 §4)                  -> amber "slow connection" notice, links to /field/sync
//   all clear (queue empty, no failures)   -> calm green "safe to finish" bar
//   queue, no failures, ONLINE              -> the call-to-action: "Tap to upload"
//   queue, no failures, OFFLINE             -> neutral slate "waiting for signal" bar (no CTA)
//
// Degraded sits below hasFailures but above the other three: a real failure
// needing attention is more urgent than a connectivity notice, but a
// queue-related bar underneath a "connection's slow" bar right now would
// just be visual noise (ADR-0110 §4 acceptance).
//
// The all-clear state is the normal/default and MUST be shown, not hidden:
// its absence is what makes people anxious and re-check obsessively (ADR-0048 §1).
//
// CTA decoupling: tapping the online CTA dispatches `field:sync-now` on window.
// field_sync_controller listens for it and runs the one shared sync(). The banner
// never duplicates upload logic.
//
// Re-renders on field:online, field:offline, field:sync-complete, and
// field:health-changed (Card 1, ADR-0110), matching the existing field
// controllers' add/remove-listener lifecycle. No special "recovery" code is
// needed for Degraded clearing -- the field:health-changed re-render just
// falls through to whichever of the other four states is current once level
// returns to Online.
//
// Compact variant (card #3863): `data-field-sync-banner-variant-value="compact"`
// on field/shared/_sync_queue_bar.html.erb, the subtle bar on Home, Today,
// Places and Activity. It never builds markup: the partial holds every state as a hidden
// section and this controller only toggles `hidden`, writes text and sets the
// progress value. Its rules differ from the five states above on purpose (the
// bar is hidden when all is clear, Degraded never outranks the queue, and the
// noun is "survey"): see the "Compact variant" section below. The full banner
// on the Sync page is unchanged.

// Exported so sign-out (field_log_event_controller.js) can clear it.
export const SNAPSHOT_KEY  = "field:sync-bar"
const DWELL_MS             = 600   // minimum time a state stays up before a calmer one replaces it
const SAVED_MS             = 6000  // how long "All photos saved" stays before the bar closes
const COLLAPSE_MS          = 200   // height animation; matches duration-200 in the partial
const INSTANT_RECONCILE_MS = 300   // a first IndexedDB read this quick applies with no animation
const PROGRESS_REREAD_MS   = 500   // throttle for re-reading the queue on upload progress ticks
const UPLOAD_HOLD_MS       = 2500  // gap between two photos of one pass still reads as uploading
const UPLOAD_LIVE_MS       = 60000 // an "uploading" row older than a photo's upload timeout is stale
const SYNC_RECHECK_MS      = 1000  // while a sync pass holds the lock, re-read this often
const SLOT_TRANSITION      = "transition-[grid-template-rows]"
const COMPACT_EVENTS       = [
  "field:online", "field:offline", "field:health-changed",
  "field:offline-mode-changed", "field:queue-changed"
]

export default class extends Controller {
  static targets = ["slot", "state", "progress"]
  static values  = { variant: String, attentionSelector: String }

  connect() {
    if (this.variantValue === "compact") return this._connectCompact()

    this._render()

    this._refreshHandler = () => this._render()
    window.addEventListener("field:online", this._refreshHandler)
    window.addEventListener("field:offline", this._refreshHandler)
    window.addEventListener("field:sync-complete", this._refreshHandler)
    window.addEventListener("field:health-changed", this._refreshHandler)
    window.addEventListener("field:queue-changed", this._refreshHandler)
  }

  disconnect() {
    if (this.variantValue === "compact") return this._disconnectCompact()

    window.removeEventListener("field:online", this._refreshHandler)
    window.removeEventListener("field:offline", this._refreshHandler)
    window.removeEventListener("field:sync-complete", this._refreshHandler)
    window.removeEventListener("field:health-changed", this._refreshHandler)
    window.removeEventListener("field:queue-changed", this._refreshHandler)
  }

  // Failures bar tap on a page that lists the failed items itself (/field/sync):
  // scroll to that list instead of linking to the page we are already on.
  showAttention() {
    const target = document.querySelector(this.attentionSelectorValue)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    target.focus({ preventScroll: true })
  }

  // Click handler for the online CTA. Decoupled -- fires the shared sync engine
  // via a custom event rather than re-implementing any upload logic here.
  triggerSync() {
    // userInitiated (card #3844): a tap, so the photo retry wait is cleared.
    window.dispatchEvent(new CustomEvent("field:sync-now", { detail: { userInitiated: true } }))
  }

  async _render() {
    const summary = await queueSummary()
    const online  = localStorage.getItem("field:online") !== "false"

    if (summary.hasFailures) {
      this.element.innerHTML = this._failuresBar(summary)
    } else if (currentLevel() === LEVEL_DEGRADED) {
      this.element.innerHTML = this._degradedBar()
    } else if (summary.allClear) {
      this.element.innerHTML = this._allClearBar()
    } else if (online) {
      this.element.innerHTML = this._ctaBar(summary)
    } else {
      this.element.innerHTML = this._offlineBar(summary)
    }
  }

  // -- state bars --

  _allClearBar() {
    return `
      <div class="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-emerald-700 bg-emerald-50">
        ${this._checkIcon()}
        <p class="text-sm font-semibold">Everything's uploaded – safe to finish</p>
      </div>
    `
  }

  _failuresBar(summary) {
    const n     = summary.failedResponses + summary.failedImages
    const label = `${n} ${this._plural(n, "item")} ${n === 1 ? "needs" : "need"} attention – tap to review`

    const barClass = "flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-red-700 bg-red-50 active:opacity-80"
    const content  = `
        ${this._warningIcon()}
        <p class="flex-1 text-sm font-semibold">${this._esc(label)}</p>
        ${this._chevronIcon()}
    `

    // On /field/sync the list of failed items is on the page: scroll to it.
    if (this.attentionSelectorValue) {
      return `
      <button type="button" data-action="field-sync-banner#showAttention"
              class="w-full text-left ${barClass}">${content}</button>
      `
    }

    return `
      <a href="/field/sync" data-turbo-frame="_top" class="${barClass}">${content}</a>
    `
  }

  // Level 1 (Degraded, ADR-0110 §4). Same amber family as the CTA bar below
  // (both mean "connection isn't great") but deliberately NOT a single
  // full-width tappable button like the CTA/failures bars -- this is a
  // passive notice, not an action the tasker needs to take, so it reads as
  // two lines (message + a smaller "View sync status" link) rather than one
  // giant chevron-ended CTA. That structural difference, plus the signal
  // icon (vs the CTA's upload-arrow icon), is what keeps the two amber bars
  // tellable-apart at a glance. Copy is verbatim from
  // zenith/features/field-app/training/connectivity-levels/user-training.md
  // -- do not reword without updating that doc too.
  _degradedBar() {
    return `
      <div class="flex items-start gap-2.5 px-4 py-2.5 rounded-xl text-amber-700 bg-amber-50">
        ${this._signalIcon()}
        <div class="flex-1">
          <p class="text-sm font-semibold">Slow connection detected. Your work is saving to this device and will sync in the background.</p>
          <a href="/field/sync" data-turbo-frame="_top"
             class="inline-block -mx-1 -mb-1 mt-1 px-1 py-2 text-xs font-semibold underline active:opacity-70">View sync status</a>
        </div>
      </div>
    `
  }

  // The online call-to-action. Colour choice: amber (text-amber-700 bg-amber-50).
  // The field style guide reserves bg-sapphire for solid primary buttons (the
  // "Sync now" pill); a full-width sapphire fill would read as a heavier, more
  // alarming surface than this nudge warrants. Amber matches the existing
  // "pending" semantics used across the queue (pending badges, pending-upload
  // count) -- consistent meaning, and clearly actionable without shouting.
  _ctaBar(summary) {
    const photos = summary.pendingImages + summary.uploadingImages
    const parts  = []
    if (photos > 0) parts.push(`${photos} ${this._plural(photos, "photo")}`)
    if (summary.pendingResponses > 0) {
      parts.push(`${summary.pendingResponses} ${this._plural(summary.pendingResponses, "submission")}`)
    }
    const what   = parts.join(" and ")
    const stores = `${summary.storeCount} ${this._plural(summary.storeCount, "store")}`
    const label  = `You're online – ${what} from ${stores} ready. Tap to upload.`

    return `
      <button type="button" data-action="field-sync-banner#triggerSync"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-amber-700 bg-amber-50 text-left active:opacity-80">
        ${this._uploadIcon()}
        <p class="flex-1 text-sm font-semibold">${this._esc(label)}</p>
        ${this._chevronIcon()}
      </button>
    `
  }

  _offlineBar(summary) {
    const photos = summary.pendingImages + summary.uploadingImages
    const stores = `${summary.storeCount} ${this._plural(summary.storeCount, "store")}`
    const label  = `${photos} ${this._plural(photos, "photo")} from ${stores} waiting for signal`

    return `
      <div class="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-slate-600 bg-slate-50">
        ${this._clockIcon()}
        <p class="text-sm font-semibold">${this._esc(label)}</p>
      </div>
    `
  }

  // -- icons (inline Heroicon-style, consistent with other field controllers) --

  _checkIcon() {
    return `
      <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    `
  }

  _warningIcon() {
    return `
      <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    `
  }

  // Signal-strength icon (Heroicon "signal" outline) -- distinct from the
  // upload arrow (_uploadIcon), warning triangle (_warningIcon), check
  // (_checkIcon) and clock (_clockIcon) already used by the other bars.
  _signalIcon() {
    return `
      <svg class="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" />
      </svg>
    `
  }

  _uploadIcon() {
    return `
      <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
      </svg>
    `
  }

  _clockIcon() {
    return `
      <svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    `
  }

  _chevronIcon() {
    return `
      <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
      </svg>
    `
  }

  // -- Compact variant (card #3863) --
  //
  // States, most urgent first: attention (a lost or rejected photo, a failed
  // survey), signin (photos parked for a new session), uploading, then waiting
  // (cta when online, waiting when offline). saved is transient: it follows a
  // waiting or uploading state when the queue empties because work was sent
  // (an upload seen, or a sync pass that reports successCount > 0), and holds
  // for 6 seconds. A queue that empties any other way closes straight to
  // clear. clear is closed.
  //
  // Stability: the server renders the bar closed. connect() paints the last
  // state from sessionStorage straight away (no animation), then reconciles
  // with IndexedDB. Escalation to attention, and anything replacing saved,
  // shows at once; every other change waits out DWELL_MS so the bar never
  // flickers between states.
  //
  // data-turbo-permanent moves this element between pages, which disconnects
  // and reconnects the same controller instance, so connect is idempotent:
  // listeners are added once per connect and removed on disconnect, and the
  // rendered state carries over.

  _connectCompact() {
    if (!this._view) {
      this._view    = { kind: "clear" }
      this._shownAt = -Infinity
      this._paintSnapshot()
    }
    this._connectedAt     = performance.now()
    this._firstReconcile  = true

    this._onCompactEvent  = (event) => this._queueEvent(event)
    this._onSyncComplete  = (event) => { this._lastUploadingAt = 0; this._queueEvent(event) }
    this._onProgress      = (event) => this._uploadProgress(event)
    this._onVisibility    = () => this._visibilityChanged()

    COMPACT_EVENTS.forEach(name => window.addEventListener(name, this._onCompactEvent))
    window.addEventListener("field:sync-complete", this._onSyncComplete)
    window.addEventListener("field:upload-progress", this._onProgress)
    document.addEventListener("visibilitychange", this._onVisibility)

    if (this._view.kind === "saved") this._armSavedTimer()
    this._reconcile()
  }

  _disconnectCompact() {
    COMPACT_EVENTS.forEach(name => window.removeEventListener(name, this._onCompactEvent))
    window.removeEventListener("field:sync-complete", this._onSyncComplete)
    window.removeEventListener("field:upload-progress", this._onProgress)
    document.removeEventListener("visibilitychange", this._onVisibility)
    clearTimeout(this._dwellTimer)
    clearTimeout(this._savedTimer)
    clearTimeout(this._progressTimer)
    clearTimeout(this._hideTimer)
    clearTimeout(this._recheckTimer)
    this._progressTimer = null
    this._cancelTransitionFrames()
    // Disconnecting can land between the hide timer being set and firing; the
    // next connect's reconcile re-runs _setOpen, so a closed bar still ends
    // up with every section hidden.
  }

  // A sync pass that actually sent something earns the "saved" state
  // (field:sync-complete from the page, QUEUE_CHANGED from the worker).
  _queueEvent(event) {
    if (Number(event?.detail?.successCount) > 0) this._savedEarned = true
    this._reconcile()
  }

  async _reconcile() {
    const seq = (this._seq || 0) + 1
    this._seq = seq

    let summary, syncing
    try {
      [summary, syncing] = await Promise.all([queueSummary(), this._syncRunning()])
    } catch {
      return
    }
    if (seq !== this._seq || !this.element.isConnected) return

    const instant = this._firstReconcile && performance.now() - this._connectedAt <= INSTANT_RECONCILE_MS
    this._firstReconcile = false
    this._recheckIn = null
    this._apply(this._nextView(summary, { syncing }), { instant })
    this._scheduleRecheck()
  }

  _nextView(summary, { syncing = false } = {}) {
    const now      = Date.now()
    const photos   = summary.pendingImages + summary.uploadingImages + (summary.failedImages - summary.attentionImages)
    const surveys  = summary.pendingResponses
    const network  = this._network()

    // A row only counts as uploading while its upload could still be in
    // flight. One left "uploading" after the app was killed reads as waiting
    // until recoverStuckUploads() re-arms it on the next sync.
    const liveSince  = summary.latestUploadStartedAt || 0
    const liveUpload = summary.uploadingImages > 0 && now - liveSince < UPLOAD_LIVE_MS
    if (liveUpload) {
      this._lastUploadingAt = now
      this._recheckAt(liveSince + UPLOAD_LIVE_MS - now)
    }

    if (summary.failedResponses > 0 || summary.attentionImages > 0) {
      this._batchTotal = null
      return this._attentionView(summary.failedResponses, summary.attentionImages)
    }
    if (summary.reauthRequired > 0) {
      this._batchTotal = null
      return { kind: "signin", text: "Sign in again to finish uploading", count: summary.reauthRequired }
    }

    if (photos + surveys > 0) {
      if (this._queuedSince == null) this._queuedSince = now
      if (surveys > 0) this._hadSurveys = true
      const what = this._what(surveys, photos)

      const held      = now - (this._lastUploadingAt || 0) < UPLOAD_HOLD_MS
      const uploading = network.online && (liveUpload || held)
      if (uploading) {
        // The hold has no event of its own to end it: re-read when it lapses.
        if (!liveUpload) this._recheckAt(this._lastUploadingAt + UPLOAD_HOLD_MS - now)
        return this._uploadingView(photos, surveys)
      }

      this._batchTotal = null
      if (!network.online) {
        const text = network.manual ? `${what} waiting. Offline mode is on` : `${what} waiting for signal`
        return { kind: "waiting", text, count: photos + surveys }
      }
      const text = network.degraded ? `${what} waiting. Slow connection, tap to try now` : `${what} waiting to upload`
      return { kind: "cta", text, count: photos + surveys }
    }

    // The queue is empty. "Saved" has to mean saved: only after a sync pass
    // sent something, an upload was actually seen, or a queued photo was
    // confirmed uploaded, since the queue was last empty. A row deleted or purged, or an old snapshot reconciled
    // against an empty queue, closes the bar instead.
    this._batchTotal = null
    const kind = this._view.kind
    const savedStillUp = kind === "saved" && (document.hidden || this._savedUntil > now)
    const wasQueued    = kind === "waiting" || kind === "cta" || kind === "uploading"
    const photoSent    = this._queuedSince != null && summary.latestUploadedAt >= this._queuedSince
    if (savedStillUp || (wasQueued && (this._savedEarned || photoSent))) {
      const text = this._hadSurveys ? "Everything is saved" : "All photos saved"
      return { kind: "saved", text, count: 0 }
    }
    // A survey replay never marks a row "uploading": the row is deleted
    // mid-pass and field:sync-complete follows once the pass ends. Hold the
    // current state until then rather than closing without the confirmation.
    if (wasQueued && syncing) {
      this._recheckAt(SYNC_RECHECK_MS)
      return this._view
    }
    this._savedEarned = false
    this._queuedSince = null
    return { kind: "clear", text: "", count: 0 }
  }

  // Is a sync pass (this tab, another tab or the service worker) running?
  async _syncRunning() {
    try {
      if (!navigator.locks?.query) return false
      const { held = [] } = await navigator.locks.query()
      return held.some(lock => lock.name === SYNC_LOCK_NAME)
    } catch {
      return false
    }
  }

  // Ask for one re-read in `ms`; the soonest request in a pass wins.
  _recheckAt(ms) {
    const wait = Math.max(0, ms)
    if (this._recheckIn == null || wait < this._recheckIn) this._recheckIn = wait
  }

  _scheduleRecheck() {
    clearTimeout(this._recheckTimer)
    if (this._recheckIn == null) return
    this._recheckTimer = setTimeout(() => this._reconcile(), this._recheckIn + 50)
  }

  _attentionView(surveys, photos) {
    let text
    if (surveys > 0 && photos > 0) {
      text = `${this._what(surveys, photos)} need attention`
    } else if (photos > 0) {
      text = `${this._count(photos, "photo")} couldn't be saved`
    } else {
      text = `${this._count(surveys, "survey")} ${surveys === 1 ? "needs" : "need"} a fix`
    }
    return { kind: "attention", text, count: surveys + photos }
  }

  // "2 of 3" counts against the batch as it stood when uploading first showed.
  // A photo taken mid-batch grows the total rather than resetting it.
  _uploadingView(photos, surveys) {
    const remaining = photos + surveys
    if (!this._batchTotal || remaining > this._batchTotal) {
      this._batchTotal      = remaining
      this._batchHasSurveys = surveys > 0
    }
    if (surveys > 0) this._batchHasSurveys = true

    const total = this._batchTotal
    const done  = total - remaining
    if (done !== this._batchDone) {
      this._batchDone = done
      this._pct       = 0
    }
    const current = Math.min(done + 1, total)
    const noun    = this._batchHasSurveys ? "items" : this._plural(total, "photo")
    return { kind: "uploading", text: `Uploading ${current} of ${total} ${noun}`, count: remaining, done, total }
  }

  _apply(next, { instant = false } = {}) {
    clearTimeout(this._dwellTimer)
    const current = this._view

    if (current.kind === next.kind) {
      this._show(next, { kindChanged: false, instant })
      return
    }

    const immediate = instant || next.kind === "attention" || (current.kind === "saved" && next.kind !== "clear")
    const wait      = immediate ? 0 : DWELL_MS - (performance.now() - this._shownAt)
    if (wait > 0) {
      this._dwellTimer = setTimeout(() => this._reconcile(), wait)
      return
    }
    this._show(next, { kindChanged: true, instant })
  }

  _show(view, { kindChanged, instant, fromSnapshot = false }) {
    const previous = this._view
    this._view = view
    if (view.kind === "uploading" && !fromSnapshot) this._savedEarned = true

    if (kindChanged) {
      this._shownAt = performance.now()
      if (view.kind === "saved") {
        this._savedUntil = Date.now() + SAVED_MS
        this._armSavedTimer()
      } else {
        clearTimeout(this._savedTimer)
        if (view.kind === "clear") this._hadSurveys = false
      }
    }

    // Attention and sign-in speak up once: on entry, or when the count rises.
    // Everything else is polite. Progress ticks are never announced, and a
    // snapshot repaint on a full page load is not news, so it stays polite.
    const urgent = view.kind === "attention" || view.kind === "signin"
    const rose   = !kindChanged && view.count > (previous.count || 0)
    const news   = !fromSnapshot && (kindChanged || rose)
    this.element.setAttribute("aria-live", urgent && news ? "assertive" : "polite")

    if (view.kind === "clear") {
      this._setOpen(false, instant)
    } else {
      this.stateTargets.forEach(section => {
        const active = section.dataset.kind === view.kind
        if (active) {
          const slot = section.querySelector("[data-slot='text']")
          if (slot && slot.textContent !== view.text) slot.textContent = view.text
        }
        section.hidden = !active
      })
      this._setOpen(true, instant)
    }

    if (view.kind === "uploading") this._setProgress()
    this._writeSnapshot()
  }

  // Opens and closes by animating the grid row from 0fr to 1fr. Closing
  // keeps the last state visible while it slides shut, then hides it so
  // nothing in the closed bar can take focus.
  _setOpen(open, instant) {
    const slot = this.slotTarget
    if (instant) {
      this._cancelTransitionFrames()
      slot.classList.remove(SLOT_TRANSITION)
      this._outerFrame = requestAnimationFrame(() => {
        this._outerFrame = null
        this._innerFrame = requestAnimationFrame(() => {
          this._innerFrame = null
          slot.classList.add(SLOT_TRANSITION)
        })
      })
    }
    slot.classList.toggle("grid-rows-[1fr]", open)
    slot.classList.toggle("grid-rows-[0fr]", !open)

    clearTimeout(this._hideTimer)
    if (!open) {
      this._hideTimer = setTimeout(() => {
        if (this._view.kind === "clear") this.stateTargets.forEach(section => { section.hidden = true })
      }, instant ? 0 : COLLAPSE_MS)
    }
  }

  // Cancels a pending re-enable of the slot transition and puts the class
  // straight back, so a disconnect mid-frame never leaves the bar unanimated.
  _cancelTransitionFrames() {
    if (this._outerFrame == null && this._innerFrame == null) return
    cancelAnimationFrame(this._outerFrame)
    cancelAnimationFrame(this._innerFrame)
    this._outerFrame = this._innerFrame = null
    if (this.hasSlotTarget) this.slotTarget.classList.add(SLOT_TRANSITION)
  }

  _setProgress() {
    if (!this.hasProgressTarget || !this._view.total) return
    const { done, total } = this._view
    this.progressTarget.value = Math.min(1, (done + (this._pct || 0) / 100) / total)
  }

  // Upload progress: move the hairline now, re-read the queue at most every
  // PROGRESS_REREAD_MS so "2 of 3" catches up without hammering IndexedDB.
  _uploadProgress(event) {
    this._lastUploadingAt = Date.now()
    this._pct = Number(event.detail?.pct) || 0
    if (this._view.kind === "uploading") this._setProgress()

    if (this._progressTimer) return
    this._progressTimer = setTimeout(() => {
      this._progressTimer = null
      this._reconcile()
    }, PROGRESS_REREAD_MS)
  }

  _armSavedTimer() {
    clearTimeout(this._savedTimer)
    if (document.hidden) return
    const ms = Math.max(0, (this._savedUntil || 0) - Date.now())
    this._savedTimer = setTimeout(() => this._reconcile(), ms)
  }

  // A hidden tab doesn't count towards the 6 seconds: it starts again when
  // the tasker comes back, so the confirmation is actually seen.
  _visibilityChanged() {
    if (this._view.kind !== "saved") return
    if (document.hidden) {
      clearTimeout(this._savedTimer)
      return
    }
    this._savedUntil = Date.now() + SAVED_MS
    this._writeSnapshot()
    this._armSavedTimer()
  }

  _network() {
    const read = (key) => { try { return localStorage.getItem(key) } catch { return null } }
    const online = read("field:offline_mode") === "online_only" || read("field:online") !== "false"
    return {
      online,
      manual:   !online && read("field:offline_source") === "manual",
      degraded: online && currentLevel() === LEVEL_DEGRADED
    }
  }

  // -- session snapshot: what the bar showed last, so a fresh page paints it
  //    on connect instead of opening from nothing --

  _writeSnapshot() {
    const snapshot = {
      view:          this._view,
      savedUntil:    this._view.kind === "saved" ? this._savedUntil : null,
      batchTotal:    this._batchTotal || null,
      batchSurveys:  !!this._batchHasSurveys,
      hadSurveys:    !!this._hadSurveys,
      at:            Date.now()
    }
    try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot)) } catch { /* private mode */ }
  }

  _paintSnapshot() {
    let snapshot
    try {
      snapshot = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || "null")
    } catch {
      return
    }
    const view = snapshot?.view
    if (!view || !view.kind || view.kind === "clear") return
    if (view.kind === "saved" && !(snapshot.savedUntil > Date.now())) return

    this._batchTotal      = snapshot.batchTotal || null
    this._batchHasSurveys = !!snapshot.batchSurveys
    this._hadSurveys      = !!snapshot.hadSurveys
    if (view.kind === "uploading") this._lastUploadingAt = snapshot.at || 0

    this._show(view, { kindChanged: true, instant: true, fromSnapshot: true })
    if (view.kind === "saved") {
      this._savedUntil = snapshot.savedUntil
      this._writeSnapshot()
    }
  }

  _what(surveys, photos) {
    const parts = []
    if (surveys > 0) parts.push(this._count(surveys, "survey"))
    if (photos > 0) parts.push(this._count(photos, "photo"))
    return parts.join(" and ")
  }

  _count(n, word) {
    return `${n} ${this._plural(n, word)}`
  }

  _plural(n, word) {
    return n === 1 ? word : `${word}s`
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
